import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { navigationItems } from '@/lib/navigation';
import Logo from '@/components/Logo';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleNav = (href: string) => (e: React.MouseEvent) => {
    if (href.startsWith('/#') && location.pathname === '/') {
      e.preventDefault();
      const id = href.slice(2);
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };


  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'py-3' : 'py-5'}`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className={`bg-white/95 backdrop-blur-xl border border-border rounded-2xl px-5 md:px-8 py-3 md:py-4 flex items-center justify-between transition-all duration-500 ${isScrolled ? 'shadow-lg' : 'shadow-sm'}`}>
          <Link to="/" className="flex items-center flex-shrink-0" aria-label="Hauptstadt Consulting Partners, Startseite">
            <Logo />
          </Link>

          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {navigationItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                onClick={handleNav(item.href)}
                className="nav-link text-foreground/75 hover:text-foreground transition-colors duration-300 text-[13px] font-medium tracking-wide py-2"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center">
            <Link
              to="/bewerben"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-sm px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_hsl(227_78%_52%/0.5)]"
            >
              Jetzt bewerben
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-foreground p-2"
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white/95 backdrop-blur-xl border border-border rounded-2xl mt-2 p-6 md:hidden shadow-lg"
            >
              <nav className="flex flex-col gap-1">
                {navigationItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={handleNav(item.href)}
                    className="block text-foreground font-medium py-3 border-b border-border last:border-0"
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  to="/bewerben"
                  className="mt-4 bg-primary text-primary-foreground font-semibold text-sm px-6 py-3 rounded-lg text-center"
                >
                  Jetzt bewerben
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
